import { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { getUserByUid } from "services/requests/v1/user";

import DataGrid, {
    Column,
    GroupPanel,
    Scrolling,
} from "devextreme-react/data-grid";
import { GoAiguaButton } from "components/generic";
import GoAiguaSwitch from "components/generic/goaiguaswitch";
import GoAiguaTextSearch from "components/generic/goaiguatextinput";
import UserAssigmentModal from "./userassigmentmodal";
import "react-intl-tel-input/dist/main.css";
import IntlTelInput from "react-intl-tel-input";

import { ALLOW_DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES, getEnvVariable } from "utils/environment";
import { isTokenKCApiExpired } from "utils/functions";
import { useTranslation } from "utils/hooks/use-translation.hook";
import GoAiguaSelect from "../generic/goaiguaselect";
import { GoAiguaSelectOption } from "types/generic";
import { useIntl } from "react-intl";

import {
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";

import { makeUserModalContentFunctions } from "utils/components/user-modal-content-functions";

type UserModalContentType = {
    editUserModal: boolean;
    userUid?: string;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    metadata: any;
    setMetadata: (metadata: any) => any;
    setCallbackTrigger: (callbackTrigger: boolean) => void;
    token: string;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
};

export type FormFields =
    | "firstName"
    | "lastName"
    | "email"
    | "username"
    | "password"
    | "repeatPassword"
    | "locale"
    | "telephone";

const formInitialData: Record<FormFields, string> = {
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    repeatPassword: "",
    locale: "",
    telephone: "",
};

const formErrorsInitialData: Record<FormFields, boolean> = {
    firstName: false,
    lastName: false,
    username: false,
    email: false,
    password: false,
    repeatPassword: false,
    locale: false,
    telephone: false,
};

const UserModalContent: React.FC<UserModalContentType> = ({
    editUserModal,
    userUid,
    isOpen,
    setIsOpen,
    setError,
    subscribeContent,
    unsubscribeContent,
    setMetadata,
    setCallbackTrigger,
    token,
}) => {
    const translate = useTranslation();
    const intl = useIntl();
    const [languages, setLanguages] = useState<GoAiguaSelectOption[]>();
    const [editUser, setEditUser] = useState(false);
    const [formData, setFormData] = useState(formInitialData);
    const [formErrors, setFormErrors] = useState(formErrorsInitialData);
    const [userStatus, setUserStatus] = useState(true);
    const [saveDisable, setSaveDisable] = useState<"disabled" | "enabled">(
        "disabled"
    );
    const [dataHasBeenEdited, setDataHasBeenEdited] = useState(false);

    const [phoneFormatError, setPhoneFormatError] = useState<boolean>(false);

    const [role, setRole] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [availableGrids, setAvailableGrids] = useState<any>({});

    const fillField = {
        text: `${translate("app.users.field_cant_be_empty")}`,
        email:
            formData.email === ""
                ? `${translate("app.users.field_email_cant_be_empty")}`
                : `${translate("app.users.field_email_format_invalid")}`,

        password: `${translate("app.users.field_minimum_characters")}`,
        repeatPassword: `${translate("app.users.field_password_match")}`,
        telephone: `${translate("app.users.field_phone_format_invalid")}`,
    };

    const {
        keycloak,
    }: {
        initialized: boolean;
        keycloak: Keycloak.KeycloakInstance;
    } = useKeycloak();

    const checkIfTokenIsValid = () => {
        if (isTokenKCApiExpired(token)) {
            keycloak.logout();
        }
    };

    const {
        getEditUserIsEnabled,
        handleFormDataChange,
        handleSaveClick,
        handleUserStatusSwitchChanges,
        retrieveGridsFromApplicationContainers,
        validateTelephone,
        validateFormData,
        verifyIfAnyRequiredFieldIsEmpty,
        verifyIfHasSomeError,
    } = makeUserModalContentFunctions({
        availableGrids,
        editUser,
        formData,
        formErrors,
        isOpen,
        userStatus,
        checkIfTokenIsValid,
        setCallbackTrigger,
        setDataHasBeenEdited,
        setFormData,
        setFormErrors,
        setIsOpen,
        setMetadata,
        setPhoneFormatError,
        setSaveDisable,
        setUserStatus,
    });

    const renderAvailableGrids = () => {
        if (availableGrids && userUid) {
            const availableGridsArray = Object.entries(availableGrids);
            let roles: any[] = [];
            let rowsSelectedAx: string[] = [];
            availableGridsArray.map((groups: any) => {
                groups[1].rowData.map((data: any) => {
                    roles.push({
                        title: groups[0],
                        group:
                            groups[0] === "Apps"
                                ? "GoAigua - " + data.group
                                : data.group,
                        role: data.title,
                        checked: data.checked,
                        client: data.client,
                    });
                    if (data.checked) {
                        rowsSelectedAx.push(data.title);
                    }
                });
            });

            return (
                <DataGrid
                    height={"100%"}
                    dataSource={roles}
                    allowColumnReordering={false}
                    showBorders={false}
                    rowAlternationEnabled={true}
                    showColumnLines={false}
                    showRowLines={false}
                    columnAutoWidth={false}
                    noDataText={translate("app.users.no-data")}
                >
                    <Scrolling mode="virtual" />
                    <GroupPanel visible={false} />
                    <Column
                        dataField="group"
                        caption={translate("app.users.category")}
                        dataType="string"
                        groupIndex={0}
                        groupCellRender={(params) => <>{params.value}</>}
                    />
                    <Column
                        // width={200}
                        dataField="title"
                        caption={translate("app.users.category")}
                        dataType="string"
                        cellRender={() => <></>}
                    />
                    <Column
                        dataField="role"
                        caption={translate("app.users.tools")}
                        dataType="string"
                        cellRender={(params) => params.value}
                    />
                    <Column
                        dataField="role"
                        caption={translate("app.users.status")}
                        dataType="string"
                        cellRender={(params) => (
                            <span
                                className={`c-users-table__status${
                                    params.data.checked
                                        ? "__assigned"
                                        : "__not-assigned"
                                }`}
                            >
                                {params.data.checked
                                    ? translate("app.users.assigned")
                                    : translate("app.users.not_assigned")}
                            </span>
                        )}
                    />
                    <Column
                        caption={translate("app.users.groups_and_roles")}
                        cellRender={(params) => (
                            <div
                                onClick={(e: any) => {
                                    setRole(params.row.data.client);
                                    setOpenModal(true);
                                }}
                                className="info-button"
                            >
                                {params.data.checked
                                    ? `+ ${translate(
                                          "app.users.view_groups_and_roles_assigment"
                                      )}`
                                    : `+ ${translate(
                                          "app.users.assign_groups_and_roles"
                                      )}`}
                            </div>
                        )}
                    />
                </DataGrid>
            );
        } else {
            return (
                <div className="c-create-user-modal__error-message">
                    <span className="c-create-user-modal__error-message__text">
                        {translate("app.users.message_roles")}
                    </span>
                </div>
            );
        }
    };

    const generateHorizontalLineWithTitle = (title: string) => {
        return (
            <div className="c-create-user-modal__horizontal-split">
                <div className="c-create-user-modal__horizontal-split-text">
                    {title}
                </div>
                <div className="c-create-user-modal__horizontal-split-line" />
            </div>
        );
    };

    useEffect(() => {
        const availableLanguages =
            getEnvVariable(AVAILABLE_LANGUAGES).split(",");

        const languages = availableLanguages.map((language) => {
            const label = intl.formatMessage({
                id: `app.Language.${language}`,
            });

            return { label, value: language };
        });

        setLanguages(
            languages.length && languages[0].value
                ? languages
                : [
                      {
                          label: intl.formatMessage({ id: `app.Language.en` }),
                          value: "en",
                      },
                  ]
        );

        setFormData({ ...formInitialData });
        setEditUser(editUserModal);
    }, []);

    useEffect(() => {
        checkIfTokenIsValid();
    }, [token]);

    useEffect(() => {
        (async () => {
            let grids = null;
            if (userUid && userUid !== "") {
                const contentName = "loadUserToEdit";
                subscribeContent(contentName);

                try {
                    checkIfTokenIsValid();
                    grids = await retrieveGridsFromApplicationContainers(
                        userUid
                    );
                    const user = (await getUserByUid(userUid)).data;
                    setFormData((prev) => ({
                        ...prev,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        username: user.username,
                        telephone: user.telephone,
                        locale: user.locale,
                    }));

                    setUserStatus(user.enabled);
                    unsubscribeContent(contentName);
                } catch (error) {
                    setError(contentName);
                }
            } else {
                grids = await retrieveGridsFromApplicationContainers();
                setFormData(formInitialData);
                setUserStatus(true);
            }
            setAvailableGrids(grids);
        })();
    }, [userUid]);

    useEffect(() => {
        const allFieldsAreValid = Object.entries(formData)
            .map(([fieldName, value]) => {
                if (
                    (editUserModal && fieldName === "password") ||
                    fieldName === "repeatPassword"
                )
                    return true;
                return validateFormData(value, fieldName as FormFields);
            })
            .every((isValid) => isValid);

        const hasEmptyFields = verifyIfAnyRequiredFieldIsEmpty();
        const hasError = verifyIfHasSomeError();

        if (
            !allFieldsAreValid ||
            hasEmptyFields ||
            phoneFormatError ||
            hasError
        ) {
            setSaveDisable("disabled");
        } else {
            if (dataHasBeenEdited) setSaveDisable("enabled");
        }
    }, [
        formData,
        editUserModal,
        dataHasBeenEdited,
        phoneFormatError,
        formErrors,
    ]);

    return (
        <div className="c-create-user-modal">
            <div className="c-create-user-modal__content">
                {generateHorizontalLineWithTitle(
                    `${translate("app.users.general_information")}`
                )}
                {getEditUserIsEnabled() ? (
                    <div className="c-create-user-modal__top-form">
                        <div className="c-create-user-modal__input-group">
                            <label>
                                {" "}
                                {translate("app.users.first_name")} *{" "}
                            </label>
                            <GoAiguaTextSearch
                                text={formData.firstName}
                                onChange={(value) =>
                                    handleFormDataChange(
                                        value,
                                        "firstName",
                                        true
                                    )
                                }
                                placeholder={translate(
                                    "app.users.placeholder.first_name"
                                )}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.firstName && fillField.text}
                            </div>
                        </div>

                        <div className="c-create-user-modal__input-group">
                            <label>{translate("app.users.last_name")} *</label>
                            <GoAiguaTextSearch
                                text={formData.lastName}
                                onChange={(value) =>
                                    handleFormDataChange(
                                        value,
                                        "lastName",
                                        true
                                    )
                                }
                                placeholder={translate(
                                    "app.users.placeholder.last_name"
                                )}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.lastName && fillField.text}
                            </div>
                        </div>

                        <div className="c-create-user-modal__input-group">
                            <label>{translate("app.users.username")} *</label>
                            <GoAiguaTextSearch
                                text={formData.username}
                                onChange={(value) =>
                                    handleFormDataChange(value, "username")
                                }
                                placeholder={translate(
                                    "app.users.placeholder.username"
                                )}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.username && fillField.text}
                            </div>
                        </div>

                        <div className="c-create-user-modal__input-group">
                            <label>
                                {translate("app.users.email_address")} *
                            </label>
                            <GoAiguaTextSearch
                                text={formData.email}
                                onChange={(value) =>
                                    handleFormDataChange(value, "email")
                                }
                                placeholder={translate(
                                    "app.users.placeholder.email"
                                )}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.email && fillField.email}
                            </div>
                        </div>
                        <div className="c-create-user-modal__buttons">
                            <small>
                                * {translate("app.users.required_fields")}
                            </small>
                            <div className="c-create-user-modal__buttons__buttons">
                                <GoAiguaButton
                                    text={translate("app.gis.cancel")}
                                    onClick={() => setIsOpen(!isOpen)}
                                    className="c-create-user-modal__buttons__cancel"
                                />
                                <GoAiguaButton
                                    text={`${translate("app.users.save")}`}
                                    onClick={handleSaveClick}
                                    status={saveDisable}
                                    className="c-create-user-modal__buttons__save"
                                />
                            </div>
                        </div>

                        <div className="c-create-user-modal__input-group">
                            <label> {translate("app.users.password")} * </label>
                            <GoAiguaTextSearch
                                text={formData.password}
                                onChange={(value) => {
                                    handleFormDataChange(value, "password");
                                }}
                                onBlur={() => {
                                    handleFormDataChange(
                                        formData.password,
                                        "password",
                                        false,
                                        true
                                    );
                                    handleFormDataChange(
                                        formData.repeatPassword,
                                        "repeatPassword"
                                    );
                                }}
                                placeholder={
                                    editUserModal
                                        ? "********"
                                        : translate(
                                              "app.users.placeholder.password"
                                          )
                                }
                                type={"password"}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.password && fillField.password}
                            </div>
                        </div>

                        <div className="c-create-user-modal__input-group">
                            <label>
                                {translate("app.users.repeat_password")} *
                            </label>
                            <GoAiguaTextSearch
                                text={formData.repeatPassword}
                                onChange={(value) => {
                                    handleFormDataChange(
                                        value,
                                        "repeatPassword"
                                    );
                                }}
                                placeholder={
                                    editUserModal
                                        ? "********"
                                        : translate(
                                              "app.users.placeholder.repeat_password"
                                          )
                                }
                                type={"password"}
                            />
                            <div className="c-create-user-modal__fill-field">
                                {formErrors.repeatPassword &&
                                    fillField.repeatPassword}
                            </div>
                        </div>

                        {languages && getEnvVariable(ALLOW_DEFAULT_LANGUAGE) !== "false" && (
                            <div className="c-create-user-modal__input-group">
                                <label>
                                    {translate("app.users.language")} *
                                </label>
                                <div className="c-create-user-modal__input-select">
                                    <GoAiguaSelect
                                        placeholder={translate(
                                            "app.placeholder"
                                        )}
                                        selectedValue={formData.locale}
                                        selectedOption={formData.locale}
                                        setSelectedOption={(value) => {
                                            handleFormDataChange(
                                                value,
                                                "locale"
                                            );

                                            setDataHasBeenEdited(true);
                                        }}
                                        options={languages}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="c-create-user-modal__input-group">
                            <label>{translate("app.users.telephone")}</label>
                            <div className="c-create-user-modal__input-phone">
                                <IntlTelInput
                                    value={
                                        formData.telephone
                                            ? formData.telephone
                                            : ""
                                    }
                                    inputClassName="c-create-user-modal__input-phone__input"
                                    onPhoneNumberChange={(
                                        numberIsValid,
                                        value,
                                        country,
                                        fullNumber
                                    ) => {
                                        handleFormDataChange(
                                            fullNumber,
                                            "telephone"
                                        );

                                        validateTelephone(
                                            numberIsValid,
                                            fullNumber
                                        );
                                        setDataHasBeenEdited(true);
                                    }}
                                    placeholder=""
                                    defaultCountry={formData.locale}
                                />
                            </div>
                            <div className="c-create-user-modal__fill-field">
                                {phoneFormatError && fillField.telephone}
                            </div>
                        </div>
                        <div className="c-create-user-modal__user-status">
                            <label>
                                {translate("app.users.inactive_active_user")}
                            </label>
                            <div className="c-create-user-modal__user-status__switch">
                                <GoAiguaSwitch
                                    switched={userStatus}
                                    onClick={handleUserStatusSwitchChanges}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    `${intl.formatMessage({
                        id: "app.users.not_enabled",
                    })}`
                )}
                {generateHorizontalLineWithTitle(
                    `${translate("app.users.text_permissions")}`
                )}
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="c-create-user-modal__table-container"
                >
                    {renderAvailableGrids()}
                </div>
                <div className="c-create-user-modal__switch"></div>
            </div>
            <UserAssigmentModal
                provenance={role}
                openModal={openModal}
                userUid={userUid}
                subscribeContent={subscribeContent}
                unsubscribeContent={unsubscribeContent}
                setError={setError}
                setOpenModal={async (open: boolean) => {
                    setOpenModal(open);
                    const grids = await retrieveGridsFromApplicationContainers(
                        userUid
                    );
                    setAvailableGrids(grids);
                }}
            />
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        token: state.keycloak.token,
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(UserModalContent);
